'use client';

/**
 * 1:1 port of legacy
 * `flows/bio/pages/contact-card/functions/vcfUtils.ts` — minus
 * the native code paths (Platform.OS !== 'web') since we're web
 * only.
 *
 * Generates a VCF 3.0 payload from the creator's
 * `store.contactCard` document. Used by:
 *   - The contact-card preview modal's QR code (visitors scan
 *     it with a phone camera → import contact).
 *   - The "Download my contact details" button (writes a `.vcf`
 *     file to the browser).
 */
export type ContactCardVcfData = {
  firstName?: string | null;
  lastName?: string | null;
  nickName?: string | null;
  email?: string | null;
  phone?: string | null;
  url?: string | null;
  url2?: string | null;
  companyName?: string | null;
  address?: string | null;
  socialLinks?: string[] | null;
  photoBase64?: string | null;
  photoType?: string | null;
};

function escapeVcfValue(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Best-effort host → social-platform mapping so the VCF can
 * tag each social URL with the right `TYPE=<platform>`
 * parameter. Mirrors legacy `kSocialLinkConfig` keys (only the
 * subset our renderer recognises).
 */
const SOCIAL_TYPES: Record<string, string> = {
  'facebook.com': 'facebook',
  'instagram.com': 'instagram',
  'twitter.com': 'twitter',
  'x.com': 'twitter',
  'tiktok.com': 'tiktok',
  'youtube.com': 'youtube',
  'youtu.be': 'youtube',
  'snapchat.com': 'snapchat',
};

function getSocialMediaType(url: string): string | null {
  const lower = url.toLowerCase();
  for (const domain of Object.keys(SOCIAL_TYPES)) {
    if (lower.includes(domain)) return SOCIAL_TYPES[domain];
  }
  return null;
}

export function generateVcfContent(
  contactCard: ContactCardVcfData | null | undefined,
): string {
  const lines: string[] = ['BEGIN:VCARD', 'VERSION:3.0'];

  if (!contactCard) {
    lines.push('N:;;;;');
    lines.push('FN:');
    lines.push('END:VCARD');
    return lines.join('\r\n');
  }

  const fullName = [contactCard.firstName || '', contactCard.lastName || '']
    .filter(Boolean)
    .join(' ');

  // `N` (structured Family;Given;…) MUST precede `FN` (display name). When the
  // human-readable `FN` comes first, iOS/Android seed the given-name from the
  // whole `FN` string and don't re-split from `N` — dumping "First Last" into
  // the First Name field on scan. No `CHARSET=UTF-8` param either: that's a
  // vCard 2.1 construct, illegal under VERSION:3.0 (UTF-8 is implicit).
  lines.push(
    `N:${escapeVcfValue(contactCard.lastName || '')};${escapeVcfValue(
      contactCard.firstName || '',
    )};;;`,
  );
  lines.push(`FN:${escapeVcfValue(fullName)}`);

  if (contactCard.nickName) {
    lines.push(`NICKNAME:${escapeVcfValue(contactCard.nickName)}`);
  }

  if (contactCard.photoBase64) {
    const photoType = contactCard.photoType || 'JPEG';
    lines.push(`PHOTO;ENCODING=b;TYPE=${photoType}:${contactCard.photoBase64}`);
  }

  if (contactCard.email) {
    lines.push(`EMAIL;type=WORK,INTERNET:${escapeVcfValue(contactCard.email)}`);
  }

  if (contactCard.phone) {
    const cleanPhone = contactCard.phone.replace(/[\s\-()]/g, '');
    lines.push(`TEL;TYPE=CELL:${escapeVcfValue(cleanPhone)}`);
  }

  if (contactCard.address) {
    lines.push(`ADR;TYPE=WORK:;;${escapeVcfValue(contactCard.address)};;;;`);
  }

  if (contactCard.companyName) {
    lines.push(`ORG:${escapeVcfValue(contactCard.companyName)}`);
  }

  if (contactCard.url) {
    lines.push(`URL;type=WORK:${escapeVcfValue(contactCard.url)}`);
  }
  if (contactCard.url2) {
    lines.push(`URL;type=WORK:${escapeVcfValue(contactCard.url2)}`);
  }

  if (contactCard.socialLinks && contactCard.socialLinks.length > 0) {
    for (const url of contactCard.socialLinks) {
      if (!url) continue;
      const socialType = getSocialMediaType(url);
      if (socialType) {
        lines.push(`URL;TYPE=${socialType}:${escapeVcfValue(url)}`);
      } else {
        lines.push(`URL:${escapeVcfValue(url)}`);
      }
    }
  }

  lines.push(`REV:${new Date().toISOString()}`);
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

/**
 * Re-fetch the avatar URL → resize to 200×200 → JPEG base64 so
 * it embeds cleanly inside the VCF `PHOTO` field. 1:1 with
 * legacy `fetchImageAsBase64WithType` (web branch).
 */
export async function fetchImageAsBase64WithType(
  imageUrl: string,
): Promise<{ base64: string; type: string } | null> {
  if (!imageUrl) return null;
  if (typeof document === 'undefined') return null;

  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }

        const maxSize = 200;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        const match = dataUrl.match(/^data:image\/([^;]+);base64,(.+)$/);
        if (match) {
          resolve({ type: match[1].toUpperCase(), base64: match[2] });
        } else {
          resolve(null);
        }
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);

    const separator = imageUrl.includes('?') ? '&' : '?';
    img.src = `${imageUrl}${separator}t=${Date.now()}`;
  });
}

/**
 * Download a `.vcf` file in the browser. 1:1 with legacy
 * `downloadVcfFileOnWeb`.
 */
export function downloadVcfFileOnWeb(
  vcfContent: string,
  filename: string,
): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([vcfContent], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Complete download helper: fetches the avatar as base64,
 * generates the VCF, triggers a browser download. 1:1 with
 * legacy `downloadContactCardVcf`.
 */
export async function downloadContactCardVcf({
  contactCardData,
  avatarUrl,
}: {
  contactCardData: ContactCardVcfData;
  avatarUrl?: string | null;
}): Promise<void> {
  let photoBase64: string | null = null;
  let photoType: string | null = null;
  if (avatarUrl) {
    const photoData = await fetchImageAsBase64WithType(avatarUrl);
    if (photoData) {
      photoBase64 = photoData.base64;
      photoType = photoData.type;
    }
  }

  const vcfContent = generateVcfContent({
    ...contactCardData,
    photoBase64,
    photoType,
  });

  const firstName = contactCardData.firstName || '';
  const lastName = contactCardData.lastName || '';
  const filename = `${firstName}_${lastName}_contact.vcf`.replace(
    /[^a-zA-Z0-9._-]/g,
    '_',
  );
  downloadVcfFileOnWeb(vcfContent, filename);
}
